import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { timeAgo } from "../lib/utils-tv";

const API = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 5 * 60 * 1000;
const NEW_WINDOW_DAYS = 14;
const MAX_NEW = 8;
const MAX_TRENDING = 8;
const STORAGE_KEY = "tv.discovery.tab";

/**
 * Single consolidated discovery strip replacing the previous trio of
 * stacked rails ([trending], [new], [tickers]). Tabbed UI keeps the
 * brutalist single-column layout while letting us add future facets
 * (e.g. [companies], [builders]) without growing vertically.
 *
 * Tabs are hidden when their underlying dataset is empty so the rail
 * is never showing dead labels. Default tab is the first non-empty one
 * in priority order: trending → new → tickers. User selection persists
 * in localStorage so power users don't have to re-pick on every visit.
 */
export default function DiscoveryStrip({ tools }) {
  const [trending, setTrending] = useState([]);
  const [trendingWindow, setTrendingWindow] = useState(7);

  // Background fetch — endpoint is cheap and fails silently.
  useEffect(() => {
    let alive = true;
    const load = () =>
      fetch(`${API}/api/trending?days=7&limit=${MAX_TRENDING}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (!alive || !d) return;
          setTrending(d.tools || []);
          if (d.window_days) setTrendingWindow(d.window_days);
        })
        .catch(() => {});
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  // `new` is computed off the tools the page already has — no extra fetch.
  const recent = useMemo(() => {
    if (!tools || tools.length === 0) return [];
    const cutoff = Date.now() - NEW_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    return tools
      .filter((t) => {
        const ts = new Date(t.approved_at || t.submitted_at).getTime();
        return !Number.isNaN(ts) && ts >= cutoff;
      })
      .sort(
        (a, b) =>
          new Date(b.approved_at || b.submitted_at).getTime() -
          new Date(a.approved_at || a.submitted_at).getTime(),
      )
      .slice(0, MAX_NEW);
  }, [tools]);

  // Build tab list in priority order; skip any with no data.
  const tabs = useMemo(() => {
    const arr = [];
    if (trending.length) {
      arr.push({ key: "trending", label: "[trending]" });
    }
    if (recent.length) {
      arr.push({ key: "new", label: "[new]" });
    }
    return arr;
  }, [trending, recent]);

  // Selected tab — restore from localStorage if it's still available,
  // otherwise default to the first available tab.
  const [active, setActive] = useState(null);
  useEffect(() => {
    if (tabs.length === 0) {
      if (active !== null) setActive(null);
      return;
    }
    const saved =
      typeof window !== "undefined"
        ? window.localStorage.getItem(STORAGE_KEY)
        : null;
    const validSaved = saved && tabs.find((t) => t.key === saved);
    const next = validSaved ? saved : tabs[0].key;
    if (next !== active) setActive(next);
  }, [tabs, active]);

  const onSelect = (key) => {
    setActive(key);
    try {
      window.localStorage.setItem(STORAGE_KEY, key);
    } catch {
      // localStorage can be blocked (private mode, embedded contexts);
      // we just lose persistence in that case, not functionality.
    }
  };

  if (tabs.length === 0 || !active) return null;

  return (
    <div
      className="border-b border-[#ddd] bg-[#fafafa]"
      data-testid="discovery-strip"
    >
      <div
        className="max-w-5xl mx-auto px-4 py-1.5 text-[11px] flex items-center gap-2 overflow-x-auto whitespace-nowrap"
        style={{ scrollbarWidth: "thin" }}
      >
        {/* Tab triggers */}
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => onSelect(t.key)}
            className={
              "shrink-0 cursor-pointer " +
              (active === t.key
                ? "text-[#000] font-bold"
                : "text-[#888] hover:text-[#000]")
            }
            data-testid={`discovery-tab-${t.key}`}
            aria-pressed={active === t.key}
          >
            {t.label}
          </button>
        ))}

        <span className="text-[#ddd] shrink-0 mx-1">|</span>

        {/* Active tab content */}
        {active === "trending" && (
          <TrendingRow items={trending} window={trendingWindow} />
        )}
        {active === "new" && <NewRow items={recent} />}
      </div>
    </div>
  );
}

function TrendingRow({ items, window }) {
  return (
    <>
      <span
        className="text-[#888] shrink-0"
        title={`Most-clicked tools in the last ${window} days`}
      >
        most clicked {window}d:
      </span>
      {items.map((t, i) => (
        <span key={t.id} className="shrink-0 flex items-center gap-1">
          <Link
            to={`/tool/${t.slug}`}
            className="tv-link"
            title={t.description}
            data-testid={`trending-${t.slug}`}
          >
            {t.title}
          </Link>
          <span className="text-[#aaa] tabular-nums">{t.clicks}↗</span>
          {i < items.length - 1 && <span className="text-[#bbb]">·</span>}
        </span>
      ))}
    </>
  );
}

function NewRow({ items }) {
  return (
    <>
      <span className="text-[#888] shrink-0">last {NEW_WINDOW_DAYS}d:</span>
      {items.map((t, i) => (
        <span key={t.id} className="shrink-0 flex items-center gap-1">
          <Link
            to={`/?focus=${t.id}`}
            className="tv-link"
            title={t.description}
            data-testid={`recent-${t.id}`}
          >
            {t.title}
          </Link>
          <span className="text-[#aaa]">
            {timeAgo(t.approved_at || t.submitted_at)}
          </span>
          {i < items.length - 1 && <span className="text-[#bbb]">·</span>}
        </span>
      ))}
    </>
  );
}
